import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Leaf, Gem, HeartHandshake } from "lucide-react";
import { Reveal, Overline, Sparkle, EASE } from "@/components/Reveal";

const IMG = {
  detail: "https://images.pexels.com/photos/6466489/pexels-photo-6466489.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  minimal: "https://images.pexels.com/photos/29012619/pexels-photo-29012619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hero: "https://images.unsplash.com/photo-1704040686428-7534b262d0d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBwcmlzdGluZSUyMGxpdmluZyUyMHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3OTIyMDA1NzN8MA&ixlib=rb-4.1.0&q=85",
};

const VALUES = [
  { icon: ShieldCheck, title: "Security Vetted", desc: "Every specialist is background-checked, insured and trained to our in-house protocol." },
  { icon: Leaf, title: "Eco-Conscious", desc: "Plant-based, family-safe products that are kind to your home and to Aotearoa." },
  { icon: Gem, title: "Precision Standards", desc: "A 50-point checklist on every visit — nothing escapes the golden eye." },
  { icon: HeartHandshake, title: "Meticulous Care", desc: "We treat your space as if it were our own, down to the way cushions are set." },
];

const SUBURBS = ["Ponsonby", "Remuera", "Parnell", "Takapuna", "Auckland CBD", "Epsom", "Herne Bay", "Devonport", "Newmarket", "Mission Bay", "Grey Lynn", "Mt Eden"];

export default function About() {
  return (
    <motion.main
      data-testid="about-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Overline>Our Story</Overline>
              <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl lg:text-6xl">
                Five-star housekeeping, brought to <em className="text-gold-gradient not-italic">business</em>
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-sm leading-relaxed text-stone sm:text-base">
                TidyCare was founded in Auckland on a simple belief: every
                workplace deserves the calm, immaculate standard of a five-star
                hotel. From executive offices and CBD suites to retail showrooms
                and studios, our specialists bring hotel-grade housekeeping
                protocol to the commercial spaces where Auckland does business.
              </p>
              <p className="mt-5 text-sm leading-relaxed text-stone sm:text-base">
                A spotless workplace speaks before a word is said — to your
                clients, your team and your partners. That's why we obsess over
                the details others miss: gleaming glass, polished floors, fresh
                meeting rooms and reception spaces that make the right first
                impression, every single day. The same golden standard extends
                to the homes of our regular clients across the city.
              </p>
              <Link
                to="/contact"
                data-testid="about-quote-btn"
                className="btn-gold mt-9 inline-block rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.2em]"
              >
                Experience TidyCare
              </Link>
            </Reveal>
          </div>
          <div className="relative lg:col-span-6">
            <Reveal delay={0.2}>
              <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-gold-champagne/40 shadow-[0_40px_80px_-30px_rgba(154,123,56,0.45)]">
                <img src={IMG.detail} alt="TidyCare specialist detailing a luxury interior" className="h-[26rem] w-full object-cover lg:h-[30rem]" data-testid="about-story-image" />
              </div>
              <div className="absolute -bottom-8 -left-4 hidden w-44 overflow-hidden rounded-2xl border border-gold-champagne/50 shadow-xl lg:block">
                <img src={IMG.minimal} alt="Pristine minimal interior" className="h-40 w-full object-cover" />
              </div>
              <Sparkle className="absolute -right-3 -top-3 h-9 w-9 text-gold" />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="values-section" className="bg-obsidian py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="text-center">
            <Overline dark>What Guides Us</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ivory sm:text-4xl lg:text-5xl">
              The golden principles
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div
                  data-testid={`value-${i}`}
                  className="h-full rounded-3xl border border-ivory/10 bg-white/5 p-8 backdrop-blur-sm transition-colors duration-500 hover:border-gold/40"
                >
                  <v.icon size={26} className="text-gold" />
                  <h3 className="mt-5 font-serif text-xl font-medium text-ivory">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/60">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="coverage-section" className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Auckland Coverage</Overline>
              <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">
                Across the isthmus &amp; the Shore
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-stone sm:text-base">
                Our teams are based across central Auckland and the North Shore,
                so we're never far away. If your suburb isn't listed, just ask —
                we regularly travel further for regular clients.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-3">
              {SUBURBS.map((s, i) => (
                <Reveal key={s} delay={i * 0.04}>
                  <span
                    data-testid={`suburb-${i}`}
                    className="rounded-full border border-sand bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink/75 transition-colors duration-300 hover:border-gold/60 hover:text-gold-dark"
                  >
                    {s}
                  </span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </motion.main>
  );
}
